import { html, property, query } from 'lit-element';
import {
  KatLitElement,
  register,
  dateToStringConverter,
  event,
  EventEmitter,
} from '../../shared/base';
import { ifNotNull } from '../../utils/directives';
import baseStyles from '../../shared/base/base.lit.scss';
import { KatDatePicker } from '../date-picker';
import styles from './date-range-picker.lit.scss';

/**
 * @component {kat-date-range-picker} KatalDaterangepicker Allows a user to select a range of dates and provides a display for the selected dates.
 * @guideline Do Best used when you want to collect a range of dates
 * @example UsLocale {"locale": "en-US"}
 * @example GermanLocale {"locale": "de-DE"}
 * @example DisabledWeekends {"id": "disabled-weekends", "locale": "en-US", "start-label": "Disabled Weekends",
 * "script": "
 *   var rangePicker = document.querySelector(\"kat-date-range-picker\");
 *   var disableWeekends = function (date) {
 *     return (date.getDay() === 0 || date.getDay() === 6);
 *   };
 *   rangePicker.startPicker.isDateDisabled = disableWeekends;
 *   rangePicker.endPicker.isDateDisabled = disableWeekends;
 * "}
 *  @example ColorCodedDates {"locale": "en-US", "label": "Color coded dates",
 * "script": "
 *   var SCARY_DAY_OF_MONTH = 13;
 *   document.querySelector(\"kat-date-range-picker\").getDateDecorationConfig = function (date) {
 *     if (date.getDate() === SCARY_DAY_OF_MONTH) {
 *         return {style: 'color-code-01', ariaLabel: 'Do not book today.'};
 *     }
 *     return null;
 *   };
 * "}
 * @status Production
 * @theme flo
 */
@register('kat-date-range-picker')
export class KatDateRangePicker extends KatLitElement {
  /** Set the start date value for the date pickers */
  @property({
    type: String,
    attribute: 'start-value',
    converter: dateToStringConverter,
  })
  startValue?: string | Date;

  /** Set the end date value for the date pickers */
  @property({
    type: String,
    attribute: 'end-value',
    converter: dateToStringConverter,
  })
  endValue?: string | Date;

  /**
   * The name of the start field, used when
   * sending form data to a server.
   */
  @property({ attribute: 'start-name' })
  startName?: string;

  /**
   * The name of the end field, used when
   * sending form data to a server.
   */
  @property({ attribute: 'end-name' })
  endName?: string;

  /** Set the locale for the date pickers */
  @property()
  locale = 'en-US';

  /** Set the label for the end date pickers */
  @property({ attribute: 'end-label' })
  endLabel: string;

  /** Set the label for the start date pickers */
  @property({ attribute: 'start-label' })
  startLabel: string;

  /**
   * Used to define a function that is called each time a day is rendered.
   * Passed the date the day represents.
   * Returns a configuration object that contains the date rendering style and accessibility content
   * or null if no specific styling is applicable to the date.
   * style attribute must be one of "color-code-01" | "color-code-02" | "color-code-03" | "color-code-04" | "color-code-05" |
   * "color-code-06" | "color-code-07" | "color-code-08" | "color-code-09" | "color-code-10".
   *
   * Each style must be accompanied with a corresponding accessibility blurb that describes the specifics of the date.
   *
   * E.G. {style: 'color-code-01', ariaLabel: 'Bookings today require payment.'}
   */
  @property({ attribute: false })
  getDateDecorationConfig?: (
    date: Date
  ) => { style: string; ariaLabel: string } | null;

  /**
   * Specifies the autocomplete behaviour that a browser should attempt:
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  @property()
  autocomplete?: string;

  /**
   * @classprop {HTMLElement} startPicker The first date picker element that represents the start date.
   */
  @query('.start')
  readonly startPicker: KatDatePicker;

  /**
   * @classprop {HTMLElement} endPicker The second date picker element that represents the end date.
   */
  @query('.end')
  readonly endPicker: KatDatePicker;

  /** Fired when either date is updated. */
  @event('change', true)
  private _change: EventEmitter<{
    startValue: string | Date;
    endValue: string | Date;
    startDate: string;
    endDate: string;
  }>;

  get updateComplete() {
    return super.updateComplete.then(() =>
      Promise.all([
        this.startPicker.updateComplete,
        this.endPicker.updateComplete,
      ])
    );
  }

  static get styles() {
    return [baseStyles, styles];
  }

  _startDateChange(e) {
    e.stopImmediatePropagation();
    this.startValue = e.detail.value;
    this._change.emit(this._getRangeState());
  }

  _endDateChange(e) {
    e.stopImmediatePropagation();
    this.endValue = e.detail.value;
    this._change.emit(this._getRangeState());
  }

  _getRangeState() {
    const start = this.startPicker.dateValue;
    const end = this.endPicker.dateValue;
    return {
      startValue: this.startValue,
      endValue: this.endValue,
      startDate: start ? start.toISOString() : '',
      endDate: end ? end.toISOString() : '',
    };
  }

  _getSpanSlot(slotName) {
    let checkSlot = this.querySelector(`span.${slotName}`);
    if (!checkSlot) {
      checkSlot = document.createElement('span');
      checkSlot.setAttribute('class', slotName);
      checkSlot.setAttribute('slot', 'private-light-dom');
      this.appendChild(checkSlot);
    }

    return checkSlot;
  }

  firstUpdated() {
    // Tell each date picker to put it's hidden input into the date range pickers light DOM
    this.startPicker._overrideHiddenInputTarget = this._getSpanSlot(
      'start-slot'
    );
    this.endPicker._overrideHiddenInputTarget = this._getSpanSlot('end-slot');
  }

  updated(changedProperties) {
    if (changedProperties.has('startValue')) {
      this.startPicker.value = this.startValue;
    }
    if (changedProperties.has('endValue')) {
      this.endPicker.value = this.endValue;
    }
    if (changedProperties.has('getDateDecorationConfig')) {
      this.startPicker.getDateDecorationConfig = this.getDateDecorationConfig;
      this.endPicker.getDateDecorationConfig = this.getDateDecorationConfig;
    }
  }

  render() {
    return html`
      <kat-date-picker
        class="start"
        name=${ifNotNull(this.startName)}
        locale=${ifNotNull(this.locale)}
        label=${ifNotNull(this.startLabel)}
        getDateDecorationConfig=${this.getDateDecorationConfig}
        autocomplete=${ifNotNull(this.autocomplete)}
        @change=${this._startDateChange}
      ></kat-date-picker>
      <kat-date-picker
        class="end"
        name=${ifNotNull(this.endName)}
        locale=${ifNotNull(this.locale)}
        label=${ifNotNull(this.endLabel)}
        getDateDecorationConfig=${this.getDateDecorationConfig}
        autocomplete=${ifNotNull(this.autocomplete)}
        @change=${this._endDateChange}
      ></kat-date-picker>
      <slot name="private-light-dom"></slot>
    `;
  }
}
